import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Show } from '@refinedev/antd';
import { useShow } from '@refinedev/core';
import { Descriptions, Tag, Table, Input, Button, Space, App as AntdApp } from 'antd';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { BidiText, MonoCode, formatDateBidi } from '@lebinatikra/rtl-utils';
import { groupsApi } from '../../lib/groups-api';
import { FilterApiError } from '../../lib/filter-api-client';

interface GroupMember {
  user_id: string;
  email: string | null;
  role: string | null;
  joined_at: string;
}

interface GroupRow {
  id: string;
  organization_id: string;
  name: string;
  type: string;
  auto_created_via: string | null;
  created_at: string;
  members?: GroupMember[];
}

export function GroupShow() {
  const { t, i18n } = useTranslation();
  const { notification } = AntdApp.useApp();
  const { organizationId, id } = useParams();
  const qc = useQueryClient();
  const [newMemberId, setNewMemberId] = useState('');

  const { query } = useShow<GroupRow>({
    resource: 'groups',
    meta: { parentId: organizationId },
  });
  const record = query.data?.data;

  const reportError = (err: unknown) => {
    if (err instanceof FilterApiError) {
      notification.error({
        message: i18n.language === 'en' ? err.messageEn : err.message,
      });
    } else if (err instanceof Error) {
      notification.error({ message: err.message });
    } else {
      notification.error({ message: t('errors.generic') });
    }
  };

  const addMember = useMutation({
    mutationFn: (userId: string) => {
      if (!organizationId || !id) throw new Error('missing params');
      return groupsApi.addMember({ organizationId, groupId: id, userId });
    },
    onSuccess: () => {
      setNewMemberId('');
      void qc.invalidateQueries({ queryKey: ['groups'] });
      void query.refetch();
      notification.success({ message: t('actions.add_member') });
    },
    onError: reportError,
  });

  const removeMember = useMutation({
    mutationFn: (userId: string) => {
      if (!organizationId || !id) throw new Error('missing params');
      return groupsApi.removeMember({ organizationId, groupId: id, userId });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['groups'] });
      void query.refetch();
      notification.success({ message: t('actions.remove_member') });
    },
    onError: reportError,
  });

  return (
    <Show isLoading={query.isLoading}>
      {record ? (
        <>
          <Descriptions column={1} bordered>
            <Descriptions.Item label={t('common.id')}>
              {/* rtl-utils:bidi-wrapped */}
              <MonoCode>{record.id}</MonoCode>
            </Descriptions.Item>
            <Descriptions.Item label={t('common.name')}>
              {/* rtl-utils:bidi-wrapped */}
              <BidiText>{record.name}</BidiText>
            </Descriptions.Item>
            <Descriptions.Item label={t('fields.type')}>
              <Tag>{record.type}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('fields.auto_created_via')}>
              {record.auto_created_via ? (
                <Tag color="purple">{record.auto_created_via}</Tag>
              ) : (
                <Tag>Manual</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('common.created_at')}>
              {formatDateBidi(record.created_at)}
            </Descriptions.Item>
          </Descriptions>

          <h3 style={{ marginTop: 24 }}>{t('fields.member_count')}</h3>
          <Space style={{ marginBottom: 12 }}>
            <Input
              dir="ltr"
              placeholder="user uuid"
              value={newMemberId}
              onChange={(e) => setNewMemberId(e.target.value)}
              style={{ width: 320 }}
            />
            <Button
              type="primary"
              loading={addMember.isPending}
              disabled={!newMemberId}
              onClick={() => addMember.mutate(newMemberId)}
            >
              {t('actions.add_member')}
            </Button>
          </Space>
          <Table
            rowKey="user_id"
            dataSource={record.members ?? []}
            pagination={false}
            columns={[
              {
                dataIndex: 'user_id',
                title: t('common.id'),
                render: (v: string) => (
                  // rtl-utils:bidi-wrapped
                  <MonoCode>{v}</MonoCode>
                ),
              },
              {
                dataIndex: 'email',
                title: t('auth.email'),
                render: (v: string | null) =>
                  v ? (
                    // rtl-utils:bidi-wrapped
                    <BidiText dir="ltr">{v}</BidiText>
                  ) : (
                    '—'
                  ),
              },
              {
                dataIndex: 'role',
                title: t('fields.role'),
                render: (v: string | null) => (v ? <Tag>{v}</Tag> : '—'),
              },
              {
                dataIndex: 'joined_at',
                title: t('common.created_at'),
                render: (v: string) => formatDateBidi(v),
              },
              {
                title: t('common.actions'),
                render: (_: unknown, row: GroupMember) => (
                  <Button
                    danger
                    size="small"
                    loading={removeMember.isPending}
                    onClick={() => removeMember.mutate(row.user_id)}
                  >
                    {t('actions.remove_member')}
                  </Button>
                ),
              },
            ]}
          />
        </>
      ) : null}
    </Show>
  );
}
